import React, { useCallback, useContext, useEffect } from "react";
import {
	Box,
	Button,
	Chip,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	Link,
	MenuItem,
	Select,
	Stack,
	Typography,
	useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Path } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { bookingDataValueState, customerDataValueState, raFillingAccordionValidationState, stepState } from "../state";
import { IConsentMgmtForm } from "../../models/models";
import { FormCheckbox } from "../../common/form-fields-wrappers";
import { CONSENT_MGMT_INITIAL_VALUE, CONSENT_MGMT_VALIDATION_SCHEMA } from "./config";
import { consentManagement } from "../services/http.services";
import CustomLoader from "../../../../util/component/CustomLoader";
import { yupResolver } from "@hookform/resolvers/yup";
import FileUploader from "../../../../util/component/FileUploader";
import { useMutation } from "@tanstack/react-query";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { COMMUNICATION_CHANNELS } from "../../types";

type ConsentMgmtKey = | "extendedPromotions" | "relatedPromotions" | "commercialPromotions" | "acknowledgment";

type ChannelFieldName = | "extendedPromotionsChannels" | "relatedPromotionsChannels" | "commercialPromotionsChannels" | "acknowledgmentChannels"

const consentToChannelMap: Record<ConsentMgmtKey, ChannelFieldName> = {
	extendedPromotions: "extendedPromotionsChannels",
	relatedPromotions: "relatedPromotionsChannels",
	commercialPromotions: "commercialPromotionsChannels",
	acknowledgment: "acknowledgmentChannels"
}

const ConsentMgmt = () => {
	const theme = useTheme();
	const { t } = useTranslation(['network', 'translation']);
	const { dispatchGlobalData } = useContext(GlobalContext);
	const [step, setStep] = useRecoilState(stepState);
	const customerDataValue = useRecoilValue(customerDataValueState);
	const bookingEntity = useRecoilValue(bookingDataValueState);

	const consentMgmtForm = useForm<IConsentMgmtForm>({
		defaultValues: CONSENT_MGMT_INITIAL_VALUE,
		resolver: yupResolver(CONSENT_MGMT_VALIDATION_SCHEMA)
	});

	const showChannelsFor = ["drivalia_fr"];

	const errors: any = consentMgmtForm.formState.errors;

	const setRaFillingAccordionValidation = useSetRecoilState(raFillingAccordionValidationState);

	const viewDefaultError = (mess: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(mess),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	const consentManagementMutation = useMutation(
		consentManagement,
		{
			onSuccess: (response: any) => {
				if (!!response.data) {
					const completed = step.completed.concat();
					completed[step.active] = true;
					setStep({
						active: step.active + 1,
						completed,
						numSteps: step.numSteps
					});
				} else {
					consentMgmtForm.setValue('consents.extendedPromotionsDisagree', response.extendedPromotionsDisagree);
					consentMgmtForm.setValue('consents.extendedPromotionsAgree', response.extendedPromotionsAgree);
					consentMgmtForm.setValue('consents.extendedPromotionsChannels', Array.isArray(response.extendedPromotionsChannels) ? response.extendedPromotionsChannels : [])
					consentMgmtForm.setValue('consents.relatedPromotionsDisagree', response.relatedPromotionsDisagree);
					consentMgmtForm.setValue('consents.relatedPromotionsAgree', response.relatedPromotionsAgree);
					consentMgmtForm.setValue('consents.relatedPromotionsChannels', Array.isArray(response.relatedPromotionsChannels) ? response.relatedPromotionsChannels : []);
					consentMgmtForm.setValue('consents.commercialPromotionsDisagree', response.commercialPromotionsDisagree);
					consentMgmtForm.setValue('consents.commercialPromotionsAgree', response.commercialPromotionsAgree);
					consentMgmtForm.setValue('consents.commercialPromotionsChannels', Array.isArray(response.commercialPromotionsChannels) ? response.commercialPromotionsChannels : []);
					consentMgmtForm.setValue('acknowledgment', response.acknowledgment);
					consentMgmtForm.setValue('consents.acknowledgmentChannels', Array.isArray(response.acknowledgmentChannels) ? response.acknowledgmentChannels : []);

				}
			},
			onError: (error: any) => {
				viewDefaultError('translation:error.tryLater');
			}
		});

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (step.active > 0) {

			setStep({
				active: step.active - 1,
				completed: step.completed,
				numSteps: step.numSteps
			});

			setRaFillingAccordionValidation({
				generalInfo: true,
				firstBillingOwnerInfo: false,
				secondBillingOwnerInfo: false,
				ancillariesInfo: false
			});
		}
	}

	const CHANNEL_OTHERS = [COMMUNICATION_CHANNELS.find((ch) => (ch.label === 'OTHERS'))?.value];

	const handleNextStep = (data: IConsentMgmtForm) => {
		// upload bin file
		const form = new FormData();
		// @ts-ignore
		form.append('file', data.consents.file);

		let consentManagements: any;
		if (shouldShowChannelsForTenant(showChannelsFor)) {
			consentManagements = {
				idRentalAgreement: bookingEntity.idRentalAgreement,
				fiscalCode: customerDataValue.mainDriver.fiscalCode,
				customerId: bookingEntity.raOwner.cbId,
				idTenant: bookingEntity.idTenant,
				dealer: bookingEntity.dealer,
				acknowledgmentChannels: data.consents.acknowledgmentChannels,
				extendedPromotionsChannels: data.consents.extendedPromotionsChannels,
				relatedPromotionsChannels: data.consents.relatedPromotionsChannels,
				commercialPromotionsChannels: data.consents.commercialPromotionsChannels,
			}
		} else {
			consentManagements = {
				idRentalAgreement: bookingEntity.idRentalAgreement,
				fiscalCode: customerDataValue.mainDriver.fiscalCode,
				customerId: bookingEntity.raOwner.cbId,
				idTenant: bookingEntity.idTenant,
				dealer: bookingEntity.dealer,
				acknowledgmentChannels: data.acknowledgment ? CHANNEL_OTHERS : [],
				extendedPromotionsChannels: data.consents.extendedPromotionsAgree ? CHANNEL_OTHERS : [],
				relatedPromotionsChannels: data.consents.relatedPromotionsAgree ? CHANNEL_OTHERS : [],
				commercialPromotionsChannels: data.consents.commercialPromotionsAgree ? CHANNEL_OTHERS : [],
			}
		}

		form.append('consentManagements', JSON.stringify(consentManagements));
		consentManagementMutation.mutate(form as any);
	}

	// --- NEW: updateStepOptions adapted to this form (keeps names intact) ---
	const updateStepOptions = useCallback(
		(updates: Partial<IConsentMgmtForm["consents"]>) => {
			(Object.keys(updates) as (keyof IConsentMgmtForm["consents"])[]).forEach((key) => {
				consentMgmtForm.setValue(
					`consents.${key}` as const,
					updates[key] as any,
					{ shouldValidate: true, shouldDirty: true, shouldTouch: true }
				);
			});
		},
		[consentMgmtForm]
	);

	// handleCheckEquipment keeps existing behavior but also clears channels when agree becomes false
	const handleCheckEquipment = (e: React.SyntheticEvent, consents: string, type: "agree" | "disagree", value: boolean) => {
		e.stopPropagation();

		switch (consents) {
			case 'extended':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.extendedPromotionsDisagree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.extendedPromotionsAgree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					// if agree turned off, clear channels
					const channelKey = consentToChannelMap['extendedPromotions'];
					if (!value) {
						updateStepOptions({ [channelKey]: [] });
					} else {
						if (!shouldShowChannelsForTenant(showChannelsFor)) {
							updateStepOptions({ [channelKey]: CHANNEL_OTHERS });
						}
					}
				} else {
					consentMgmtForm.setValue('consents.extendedPromotionsAgree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.extendedPromotionsDisagree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
				}
				break;
			case 'related':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.relatedPromotionsDisagree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.relatedPromotionsAgree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					const channelKey = consentToChannelMap['relatedPromotions'];
					if (!value) {
						updateStepOptions({ [channelKey]: [] });
					} else {
						if (!shouldShowChannelsForTenant(showChannelsFor)) {
							updateStepOptions({ [channelKey]: CHANNEL_OTHERS });
						}
					}
				} else {
					consentMgmtForm.setValue('consents.relatedPromotionsAgree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.relatedPromotionsDisagree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
				}
				break;
			case 'commercial':
				if (type === 'agree') {
					consentMgmtForm.setValue('consents.commercialPromotionsDisagree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.commercialPromotionsAgree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					const channelKey = consentToChannelMap['commercialPromotions'];
					if (!value) {
						updateStepOptions({ [channelKey]: [] });
					} else {
						if (!shouldShowChannelsForTenant(showChannelsFor)) {
							updateStepOptions({ [channelKey]: CHANNEL_OTHERS });
						}
					}
				} else {
					consentMgmtForm.setValue('consents.commercialPromotionsAgree', false, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
					consentMgmtForm.setValue('consents.commercialPromotionsDisagree', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
				}
				break;
		}

		consentMgmtForm.clearErrors()
	}

	const handleUpload = async (file: File) => {
		consentMgmtForm.setValue('consents.file', file);
	}

	const handleRemove = () => {
		consentMgmtForm.setValue('consents.file', null);
	}

	useEffect(() => {
		const form = new FormData();
		form.append('consentManagements', JSON.stringify({
			fiscalCode: customerDataValue?.mainDriver?.fiscalCode,
			customerId: bookingEntity.raOwner.cbId,
			read: true,
			idTenant: bookingEntity.idTenant,
			dealer: bookingEntity.dealer,
		}));
		consentManagementMutation.mutate(form as any);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleChannelChange = useCallback(
		(key: ConsentMgmtKey) => (event: any) => {
			const value = event.target.value;
			const channelKey = consentToChannelMap[key];
			consentMgmtForm.setValue(`consents.${channelKey}` as Path<IConsentMgmtForm>, typeof value === "string" ? value.split(",") : value);
			consentMgmtForm.clearErrors(`consents.${channelKey}` as Path<IConsentMgmtForm>);
		},
		[consentMgmtForm]
	);

	const renderChannelSelection = useCallback(
		(key: ConsentMgmtKey, consentValue?: boolean | null, translationKey?: string) => {
			if (!consentValue) return null;

			const channelKey = consentToChannelMap[key];
			const selectedChannels = consentMgmtForm.watch(`consents.${channelKey}`) as string[] ?? [];
			const hasError = selectedChannels.length === 0;

			return (
				<Grid item xs={12} sx={{ mt: 1, ml: 3 }} key={`${key}-channels`}>
					<FormControl fullWidth size="small" error={hasError}>
						<FormLabel sx={{ mb: 1, fontSize: "0.875rem" }}>
							{t(`consentMgmt.consentsSection.${translationKey}Channels`)}
						</FormLabel>
						<Select
							multiple
							value={selectedChannels}
							onChange={handleChannelChange(key)}
							renderValue={(selected) => (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
									{(selected as string[]).map((value) => {
										const channel = COMMUNICATION_CHANNELS.find((ch) => ch.value === value);
										return <Chip key={value} label={channel?.label || value} size="small" />;
									})}
								</Box>
							)}
						>
							{COMMUNICATION_CHANNELS.map((channel) => (
								<MenuItem key={channel.value} value={channel.value}>
									{channel.label}
								</MenuItem>
							))}
						</Select>
						{hasError && (
							<FormHelperText>{t("consentMgmt.consentsSection.validation.selectAtLeastOne")}</FormHelperText>
						)}
					</FormControl>
				</Grid>
			);
		},
		[consentMgmtForm, handleChannelChange, t]
	);


	// determine tenant for channel visibility (keep existing default if missing)
	const tenant = bookingEntity?.idTenant ?? "drivalia_it";
	const shouldShowChannelsForTenant = (tenantToCheck: string[]) => tenantToCheck.includes(tenant);

	// Use watch to re-render channel selects when agree toggles
	const extendedAgree = consentMgmtForm.watch('consents.extendedPromotionsAgree');
	const relatedAgree = consentMgmtForm.watch('consents.relatedPromotionsAgree');
	const commercialAgree = consentMgmtForm.watch('consents.commercialPromotionsAgree');
	const acknowledgmentAgree = consentMgmtForm.watch('acknowledgment');

	return (
		<>
			<FormProvider {...consentMgmtForm}>
				<Grid container columnSpacing={2} sx={{ mb: theme.spacing(4) }}>
					<Grid item xs={12}>
						<Typography variant={"h6"}>
							{t("consentMgmt.privacySection.title")}
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<Typography variant={"body"}>
							{t("consentMgmt.privacySection.availableOn")}
						</Typography>
						<Link href="https://www.drivalia.it/it/customer-service/privacyservizio/" color={"secondary"} variant={"h6"} target={"_blank"}>
							https://www.drivalia.it/it/customer-service/privacyservizio/
						</Link>
					</Grid>

					<Grid container item xs={12} sx={{ mt: theme.spacing(2) }}>
						<Grid item xs={12}>
							<Typography variant={"body"}>
								{t("consentMgmt.privacySection.clientInfo")}
							</Typography>
						</Grid>
						<Grid item xs={12}>
							<FormCheckbox<IConsentMgmtForm>
								name={"acknowledgment"}
								label={t("consentMgmt.privacySection.acknowledgment")}
								color={'secondary'}
								sx={{ color: (errors?.acknowledgment !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							/>
						</Grid>
					</Grid>

					{/* Consents section */}
					<Grid item xs={12} sx={{ mt: theme.spacing(2) }}>
						<Typography variant={"h6"}>
							{t("consentMgmt.consentsSection.title")}
						</Typography>
					</Grid>
					<Grid item xs={12} >
						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.selfDeclaration")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{ mt: theme.spacing(2) }}>
						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.consentsInfo")}
						</Typography>
					</Grid>

					<Grid item xs={12} sx={{ mt: theme.spacing(2), pl: theme.spacing(2) }}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.extendedPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.extendedPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'extended', 'agree', value) }} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.extendedPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.extendedPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'extended', 'disagree', value) }} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.extendedPromotionsText")}
						</Typography>

						{/* Channel selection for extendedPromotions */}
						{shouldShowChannelsForTenant(showChannelsFor) &&
							renderChannelSelection('extendedPromotions', extendedAgree, 'extendedPromotions')}
					</Grid>

					<Grid item xs={12} sx={{ mt: theme.spacing(2), pl: theme.spacing(2) }}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.relatedPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.relatedPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'related', 'agree', value) }} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.relatedPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.relatedPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'related', 'disagree', value) }} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.relatedPromotionsText")}
						</Typography>

						{/* Channel selection for relatedPromotions */}
						{shouldShowChannelsForTenant(showChannelsFor) &&
							renderChannelSelection('relatedPromotions', relatedAgree, 'relatedPromotions')}
					</Grid>

					<Grid item xs={12} sx={{ mt: theme.spacing(2), pl: theme.spacing(2) }}>
						<FormCheckbox<IConsentMgmtForm>
							name={"consents.commercialPromotionsAgree"}
							label={t("consentMgmt.accepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.commercialPromotionsAgree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'commercial', 'agree', value) }} />

						<FormCheckbox<IConsentMgmtForm>
							name={"consents.commercialPromotionsDisagree"}
							label={t("consentMgmt.notAccepted")}
							color={'secondary'}
							sx={{ color: (errors?.consents?.commercialPromotionsDisagree !== undefined) ? theme.palette.error.main : theme.palette.secondary.main }}
							onChange={(e: React.SyntheticEvent, value: boolean) => { handleCheckEquipment(e, 'commercial', 'disagree', value) }} />

						<Typography variant={"body"}>
							{t("consentMgmt.consentsSection.commercialPromotionsText")}
						</Typography>

						{/* Channel selection for commercialPromotions */}
						{shouldShowChannelsForTenant(showChannelsFor) &&
							renderChannelSelection('commercialPromotions', commercialAgree, 'commercialPromotions')}
					</Grid>

					<Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: theme.spacing(2) }}>
						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
							<Stack>
								<FormLabel required={!!errors.consents?.file}
									error={!!errors.consents?.file}>
									{t("consentMgmt.uploadPaperConsents")}
								</FormLabel>
							</Stack>
							<FileUploader
								onUpload={handleUpload}
								onRemove={handleRemove}
								acceptedFiles={{ 'image/png': ['.png', '.jpeg', '.jpg'] }}
							/>

							{
								!!errors.consents?.signedMgmt &&
								<FormHelperText error>
									{t('common.error.required')}
								</FormHelperText>
							}
						</Box>

					</Grid>
				</Grid>

				<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: theme.spacing(2) }}>
					{step.active === 0 ? <Box></Box> : <Button variant={'text'} color={"primary"} onClick={handleBackStep}>{t('common.button.backStep')}</Button>}
					{step.active === (step.numSteps - 1) ? <Box></Box> : <Button variant={'outlined'} color={"accent"} onClick={consentMgmtForm.handleSubmit(handleNextStep)}>{t('common.button.nextStep')}</Button>}
				</Box>
			</FormProvider>

			<CustomLoader loading={consentManagementMutation.isLoading} />
		</>
	)
}

export default ConsentMgmt;
